import React, { useState, useEffect } from "react";
import DashboardHeader from "../components/headers/DashboardHeader";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline,
  PictureAsPdf,
  Image,
  Close,
} from "@mui/icons-material";
import {
  TextField,
  Button,
  Fade,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { styled } from "@mui/material/styles";
import { detail_user_form, submit_travel_document } from "../DAL/form/userForm";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { s3baseUri } from "../config/config";
import CloseIcon from "@mui/icons-material/Close";
import {
  image_delete_on_s3_api,
  image_upload_on_s3_api,
} from "../DAL/auth/auth";

const Input = styled("input")({
  display: "none",
});

export default function TravelDocument() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [deletedFiles, setDeletedFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({
    booking_reference: "",
    date_of_travel: null,
    traveler_name: "",
    airline_carrier: "",
    flight_number: "",
    travel_note: "",
  });

  useEffect(() => {
    if (!localStorage.getItem("code-verified")) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (newValue) => {
    const formattedDate = newValue ? moment(newValue).format("YYYY-MM-DD") : "";
    setFormData({ ...formData, date_of_travel: formattedDate });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        enqueueSnackbar("File size should be less than 5MB", {
          variant: "error",
        });
        return;
      }

      const allowedTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
      if (!allowedTypes.includes(selectedFile.type)) {
        enqueueSnackbar("Only PDF, JPG, and PNG files are allowed. Please select a valid file.", {
          variant: "error",
        });
        return;
      }

      // If we are replacing an existing S3 file, track it for deletion
      if (previewUrl && !previewUrl.startsWith("blob:")) {
        setDeletedFiles((prev) => [...prev, previewUrl]);
      } else if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setFile(selectedFile);
      if (selectedFile.type.startsWith("image/")) {
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleRemoveFile = () => {
    // If we are removing an existing S3 file, track it for deletion
    if (previewUrl && !previewUrl.startsWith("blob:")) {
      setDeletedFiles((prev) => [...prev, previewUrl]);
    } else if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(null);
    setPreviewUrl(null);
    // Reset file input value to allow selecting same file again
    const fileInput = document.getElementById("ticket-receipt-file");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file && !previewUrl) {
      enqueueSnackbar("Please upload your ticket receipt", {
        variant: "error",
      });
      return;
    }

    setIsLoading(true);

    let currentS3Path =
      !file && previewUrl && !previewUrl.startsWith("blob:") ? previewUrl : "";

    // Upload new file if exists
    if (file) {
      const uploadData = new FormData();
      uploadData.append("file", file);
      const res = await image_upload_on_s3_api(uploadData);
      if (res.code === 200) {
        currentS3Path = res.path;
      } else {
        setIsLoading(false);
        enqueueSnackbar(res.message || "Failed to upload file", {
          variant: "error",
        });
        return;
      }
    }

    // Delete old files if any
    for (const path of deletedFiles) {
      await image_delete_on_s3_api({ path });
    }

    const submitData = {
      id: form?._id,
      booking_reference: formData.booking_reference,
      date_of_travel: formData.date_of_travel,
      traveler_name: formData.traveler_name,
      airline_carrier: formData.airline_carrier,
      flight_number: formData.flight_number,
      travel_note: formData.travel_note,
      ticket_receipt: currentS3Path
        ? [
            file?.name ||
              (Array.isArray(form?.booking_info?.ticket_receipt)
                ? form.booking_info.ticket_receipt[0]
                : previewUrl?.split("/").pop()) ||
              "Document",
            currentS3Path,
          ]
        : [],
    };

    console.log(submitData, "Travel_data");

    const result = await submit_travel_document(submitData);
    if (result.code === 200) {
      setIsLoading(false);
      setIsSubmitted(true);
      setDeletedFiles([]);
      enqueueSnackbar("Travel document submitted successfully", {
        variant: "success",
      });
    } else {
      setIsLoading(false);
      enqueueSnackbar(result.message || "Failed to submit document", {
        variant: "error",
      });
    }
  };

  const handleFetchDetail = async () => {
    setIsLoading(true);
    const result = await detail_user_form();
    if (result.code === 200) {
      setForm(result?.form);
      setIsLoading(false);
    } else {
      console.error(result.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchDetail();
  }, []);

  useEffect(() => {
    if (form?.booking_info) {
      setFormData(form.booking_info);
      const receipt = form.booking_info.ticket_receipt;
      if (Array.isArray(receipt) && receipt.length > 0) {
        setPreviewUrl(receipt[1]);
      } else if (typeof receipt === "string") {
        setPreviewUrl(receipt);
      }
    }
  }, [form]);

  const cardStyle = {
    background: "#fff",
    borderRadius: "20px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
    width: "100%",
    margin: "20px auto 40px",
    padding: "40px 30px",
    transform: "translateY(0)",
    transition: "all 0.4s ease",
  };

  if (isSubmitted) {
    return (
      <div style={{ minHeight: "100vh", width: "100%" }}>
        <div className="header-container">
          <DashboardHeader />
        </div>
        <div className="container mt-5" style={{ maxWidth: "900px" }}>
          <div style={cardStyle}>
            <div className="text-center p-2 ">
              <CheckCircleOutline
                sx={{ fontSize: 70, color: "#15978a", mb: 2 }}
              />
              <h2 className="primary-color mb-2">Thank You!</h2>
              <p className="mb-5" style={{ color: "#666", fontSize: "1.1rem" }}>
                Your travel document information has been submitted
                successfully.
              </p>

              <Button
                variant="contained"
                onClick={() => navigate("/dashboard")}
              >
                Return to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading && !isSubmitted) {
    return (
      <>
        {/* <div className="header-container">
          <DashboardHeader />
        </div> */}
        <div
          className="d-flex justify-content-center align-items-center"
          // style={{ height: "88vh" }}
          style={{ height: "100vh" }}
        >
          <CircularProgress color="primary" />
        </div>
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", width: "100%" }}>
      <div className="header-container">
        <DashboardHeader />
      </div>
      <div className="container" style={{ maxWidth: "900px" }}>
        <div className="text-center mt-3 d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div>
            <IconButton onClick={() => navigate("/dashboard")}>
              <ArrowBackIcon sx={{ fontSize: 20, color: "#15978a" }} />
            </IconButton>
          </div>
          <div>
            <h2 className="primary-color mb-2">Travel Document</h2>
            <p className="mb-0">
              Submit your flight information & Ticket Receipt for review
            </p>
          </div>
          <div style={{ width: "40px" }}></div>
        </div>

        <div style={cardStyle}>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <TextField
                  label="Booking/Reference Number"
                  name="booking_reference"
                  required
                  fullWidth
                  variant="outlined"
                  value={formData.booking_reference}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: Boolean(formData.booking_reference),
                  }}
                  sx={{ mt: 1 }}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    className="date-picker"
                    label="Date of Travel"
                    disablePast={true}
                    value={
                      formData.date_of_travel
                        ? moment(formData.date_of_travel, "YYYY-MM-DD").toDate()
                        : null
                    }
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        variant: "outlined",
                        sx: { mt: 1 },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
              <div className="col-12 col-md-6 mt-3">
                <TextField
                  label="Name of Traveler"
                  name="traveler_name"
                  required
                  fullWidth
                  variant="outlined"
                  value={formData.traveler_name}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: Boolean(formData.traveler_name),
                  }}
                  sx={{ mt: 1 }}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <TextField
                  label="Airline Carrier"
                  name="airline_carrier"
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. JetBlue, American Airlines"
                  value={formData.airline_carrier}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: Boolean(formData.airline_carrier),
                  }}
                  sx={{ mt: 1 }}
                />
              </div>
              <div className="col-12 col-md-6 mt-3">
                <TextField
                  label="Flight Number"
                  name="flight_number"
                  required
                  fullWidth
                  variant="outlined"
                  placeholder="e.g. JB123"
                  value={formData.flight_number}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: Boolean(formData.flight_number),
                  }}
                  sx={{ mt: 1 }}
                />
              </div>

              <div className="col-12 mt-4">
                <h4
                  className="primary-color mb-0"
                  style={{
                    display: "block",
                  }}
                >
                  Notes (Optional)
                </h4>
                <div className="mt-1">
                  <span
                    className="mb-2"
                    style={{
                      display: "block",
                      color: "#888",
                      fontSize: "0.85rem",
                    }}
                  >
                    Any important information you want to share related to
                    ticket or documents.
                  </span>
                  <TextField
                    label="Notes"
                    name="travel_note"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={formData.travel_note || ""}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: Boolean(formData.travel_note),
                    }}
                    sx={{ mt: 1 }}
                  />
                </div>
              </div>

              <div className="col-12 mt-4">
                <h4
                  className="primary-color mb-0"
                  style={{
                    display: "block",
                  }}
                >
                  Ticket Receipt *
                </h4>
                <div className="mt-1">
                  <span
                    className="mb-3"
                    style={{
                      display: "block",
                      color: "#888",
                      fontSize: "0.85rem",
                    }}
                  >
                    Allowed formats: PDF, PNG, JPG only. Max file size: 5MB.
                  </span>

                  {!file && !previewUrl ? (
                    <label
                      htmlFor="ticket-receipt-file"
                      style={{ width: "100%", cursor: "pointer" }}
                    >
                      <Input
                        accept=".pdf,image/png,image/jpeg"
                        id="ticket-receipt-file"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        sx={{
                          py: 2,
                          borderStyle: "dashed",
                          borderWidth: "2px",
                          borderColor: "rgba(0, 0, 0, 0.15)",
                          color: "#666",
                          textTransform: "none",
                          fontSize: "1rem",
                          "&:hover": {
                            borderColor: "#15978a",
                            backgroundColor: "rgba(21, 151, 138, 0.04)",
                          },
                        }}
                      >
                        Click to upload ticket receipt
                      </Button>
                    </label>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        height: "64px",
                        paddingRight: "12px",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "8px",
                        border: "1px solid rgb(217, 217, 217)",
                        gap: "15px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {file?.type === "application/pdf" ||
                      (!file && previewUrl?.endsWith(".pdf")) ? (
                        <div
                          style={{
                            width: "64px",
                            height: "100%",
                            background: "#fff5f5",
                            borderRadius: "8px 0 0 8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRight: "1px solid #ffe3e3",
                          }}
                        >
                          <PictureAsPdf
                            sx={{ color: "#f44336", fontSize: "28px" }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "64px",
                            height: "100%",
                            background: "#e8f5f3",
                            borderRadius: "8px 0 0 8px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRight: "1px solid #cceae7",
                          }}
                        >
                          {previewUrl ? (
                            <img
                              src={
                                previewUrl?.startsWith("blob:")
                                  ? previewUrl
                                  : s3baseUri + previewUrl
                              }
                              alt="preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Image
                              sx={{ color: "#bababaff", fontSize: "28px" }}
                            />
                          )}
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span
                          className="ticket-file-text"
                          onClick={() => {
                            const url = previewUrl?.startsWith("blob:")
                              ? previewUrl
                              : s3baseUri + previewUrl;
                            window.open(url, "_blank");
                          }}
                        >
                          {(() => {
                            let name = "";
                            if (file) {
                              name = file.name; // local show
                            } else if (
                              Array.isArray(
                                form?.booking_info?.ticket_receipt
                              ) &&
                              form.booking_info.ticket_receipt.length > 0
                            ) {
                              name = form.booking_info.ticket_receipt[0];
                            } else {
                              name = previewUrl?.split("/").pop();
                            }

                            if (!name) return "";
                            return name.length > 25
                              ? `${name.substring(0, 18)}...${name.substring(
                                  name.length - 10
                                )}`
                              : name;
                          })()}
                        </span>
                      </div>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 ms-auto me-2 text-end">
              <Button
                className="mt-4"
                type="submit"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : form?.booking_info?.ticket_receipt?.length > 0 ? (
                  "Update Document"
                ) : (
                  "Submit Document"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
